import pluralize from "pluralize";
import { getUserRoleFromOrg } from "../utils/helper";

// here we fetch the collection form backend and display the schema in the form of a form
// Testing Signup

// Global Symbol value sanitization utility
const sanitizeDataForAPI = (data) => {
  if (!data) return data;
  
  try {
    // Handle arrays
    if (Array.isArray(data)) {
      return data.map(item => sanitizeDataForAPI(item)).filter(item => item !== null);
    }
    
    // Handle objects
    if (typeof data === 'object' && data !== null) {
      const sanitized = {};
      
      for (const [key, value] of Object.entries(data)) {
        // Skip Symbol keys
        if (typeof key === 'symbol') {
          console.warn('Symbol key detected in API response, skipping:', key);
          continue;
        }
        
        // Handle Symbol values
        if (typeof value === 'symbol') {
          console.warn('Symbol value detected in API response, converting to string:', key, value);
          sanitized[key] = value.toString();
          continue;
        }
        
        // Recursively sanitize nested objects and arrays
        if (value && typeof value === 'object') {
          sanitized[key] = sanitizeDataForAPI(value);
        } else {
          sanitized[key] = value;
        }
      }
      
      return sanitized;
    }
    
    // Handle primitive values
    if (typeof data === 'symbol') {
      console.warn('Primitive Symbol value detected in API response, converting to string:', data);
      return data.toString();
    }
    
    return data;
  } catch (error) {
    console.error('Error sanitizing data for API:', error);
    // Return a safe fallback
    return Array.isArray(data) ? [] : {};
  }
};

export const getNavbar = async (collectionName) => {
  const response = await fetch(
    `${process.env.REACT_APP_STRAPI_IP_ADDRESS}/api/${collectionName}/?populate=*`,
    {
      method: "GET", // *GET, POST, PUT, DELETE, etc.
      mode: "cors", // no-cors, *cors, same-origin
      cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
      // credentials: 'same-origin', // include, *same-origin, omit
      headers: {
        //   'Content-Type': 'application/json',
        // "x-jwt-token": process.env.REACT_APP_X_JWT_TOKEN,
        // 'Content-Type': 'application/x-www-form-urlencoded',
      },
      redirect: "follow", // manual, *follow, error
      referrerPolicy: "no-referrer", // no-referrer, *no-referrer-when-downgrade, origin, origin-when-cross-origin, same-origin, strict-origin, strict-origin-when-cross-origin, unsafe-url
      // body: JSON.stringify(data) // body data type must match "Content-Type" header
    }
  );

  let data = await response.json();
  return data;
};

// Retry configuration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000,
  maxDelay: 10000,
};

// Exponential backoff delay calculation
const calculateDelay = (attempt, baseDelay, maxDelay) => {
  const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
  return delay + Math.random() * 1000; // Add jitter
};

// Enhanced fetch with retry logic
const fetchWithRetry = async (url, options, attempt = 0) => {
  try {
    const response = await fetch(url, options);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`HTTP error! status: ${response.status}`);
      console.error(`Error response:`, errorText);
      
      // Retry on 5xx errors (server errors) or network issues
      if ((response.status >= 500 && response.status < 600) || response.status === 0) {
        if (attempt < RETRY_CONFIG.maxRetries) {
          const delay = calculateDelay(attempt, RETRY_CONFIG.baseDelay, RETRY_CONFIG.maxDelay);
      
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchWithRetry(url, options, attempt + 1);
        }
      }
      
      throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
    }
    
    return response;
  } catch (error) {
    // Handle network errors (socket hang up, etc.)
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      if (attempt < RETRY_CONFIG.maxRetries) {
        const delay = calculateDelay(attempt, RETRY_CONFIG.baseDelay, RETRY_CONFIG.maxDelay);
    
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchWithRetry(url, options, attempt + 1);
      }
    }
    throw error;
  }
};

export const fetchCollection = async (collectionName, filterUrl = "", jwt = "") => {
  try {
    if (!collectionName) {
      console.warn("fetchCollection: No collection name provided");
      return { data: [] };
    }

    if (!jwt) {
      try {
        jwt = JSON.parse(sessionStorage.getItem("jwtTesting"))?.jwt || "";
      } catch (error) {
        console.warn("fetchCollection: Error parsing JWT from session storage:", error);
        jwt = "";
      }
    }

    const requestBody = { collectionName, filterUrl, jwt };

    const response = await fetchWithRetry(
      `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections`,
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    const rawData = await response.json();
    
    // Sanitize the data to remove any Symbol values before returning
    const sanitizedData = sanitizeDataForAPI(rawData);
    
    // Ensure we always return a valid data structure
    if (!sanitizedData || !sanitizedData.data) {
      console.warn(`fetchCollection: Invalid response structure for ${collectionName}`, sanitizedData);
      return { data: [] };
    }

    // Additional safety check: ensure the data array doesn't contain Symbol values
    if (Array.isArray(sanitizedData.data)) {
      sanitizedData.data = sanitizedData.data.filter(item => {
        try {
          // Final check to ensure no Symbol values remain
          const hasSymbols = JSON.stringify(item).includes('Symbol(');
          if (hasSymbols) {
            console.warn('Symbol values still detected after sanitization, filtering out item:', item);
            return false;
          }
          return true;
        } catch (err) {
          console.warn('Error checking for remaining Symbol values, filtering out item:', err, item);
          return false;
        }
      });
    }

    return sanitizedData;
  } catch (error) {
    console.error(`fetchCollection: Error fetching ${collectionName}:`, error);
    
    // Return fallback data structure to prevent crashes
    return { 
      data: [],
      error: error.message,
      fallback: true 
    };
  }
};

export const getAlldata = async (collectionName) => {
  const result = await fetchCollection(collectionName);
  return result?.data || [];
};

export const getDataWithFilterUrlwithLimit = async (
  collectionName,
  filteredValue
) => {
  const url = `${filteredValue}&populate=*`;
  const result = await fetchCollection(collectionName, url);
  return result?.data || [];
};

export const getDataWithFilterUrl = async (
  collectionName,
  filteredValue,
  pageNo = 1,
  pageSize = 1000000
) => {
  // console.log("🌐 getDataWithFilterUrl called:", {
  //   collectionName,
  //   filteredValue,
  //   fullUrl: `${filteredValue}&populate=*&pagination[limit]=10000000000000000000000000`
  // });

   let url=""
  pageNo == 1 && pageSize == 1000000
    ? (url = `${filteredValue}&populate=*&pagination[limit]=1000000`)
    : (url = `${filteredValue}&populate=*&pagination[page]=${+pageNo}&pagination[pageSize]=${+pageSize}`);
  const result = await fetchCollection(collectionName, url);

  // Additional safety check for the returned data
  let safeData = result?.data || [];

  // Final verification that no Symbol values remain
  if (Array.isArray(safeData)) {
    safeData = safeData.filter((item) => {
      try {
        if (!item) return false;

        // Deep check for any remaining Symbol values
        const hasSymbols = JSON.stringify(item, (key, value) => {
          if (typeof value === "symbol") {
            console.warn(
              "Symbol value still detected in getDataWithFilterUrl result:",
              key,
              value
            );
            return null; // Mark for removal
          }
          return value;
        }).includes("null");

        if (hasSymbols) {
          console.warn("Item contains Symbol values, filtering out:", item);
          return false;
        }

        return true;
      } catch (err) {
        console.warn(
          "Error checking item for Symbol values, filtering out:",
          err,
          item
        );
        return false;
      }
    });
  }

  // console.log("📦 getDataWithFilterUrl result:", {
  //   collectionName,
  //   hasData: !!safeData,
  //   dataLength: safeData?.length || 0,
  //   result: safeData
  // });

  return safeData;
};

export const getFilterDataWithPagination = async (
  collectionName,
  filteredValue,
  pageNo = 1,
  pageSize = 100000000000
) => {
  const url = `${filteredValue}&populate=*&pagination[page]=${+pageNo}&pagination[pageSize]=${+pageSize}`;
  const result = await fetchCollection(collectionName, url);
  return result?.data || [];
};

//...
export const getAlldataInclude = async (collectionName) => {
  const url = `publicationState=preview&populate=*`;
  const result = await fetchCollection(collectionName, url);
  return result?.data || [];
};

export const getOnedata = async (collectionName, id) => {
  const url = `filters[id][$eqi]=${id}&populate=*&pagination[limit]=1000`;
  const result = await fetchCollection(collectionName, url);

  // Handle both array and object with data property
  const dataArray = result?.data || result;
  const data = (dataArray && dataArray.length > 0 && dataArray[0]) || null;

  return data;
};

export const createPayload = async (elements) => {
  const payload = { data: {} };

  (await elements) &&
    elements.forEach((data) => {
      if (data[1].type === "media" && data[3].value === "") {
        let leftkey = data[0];
        let rightkey = null;
        payload.data[leftkey] = rightkey;
      } else if (data[1].type === "relation" && data[3].value === "") {
        let leftkey = data[0];
        let rightkey = null;
        payload.data[leftkey] = rightkey;
      } else {
        let leftkey = data[0];
        let rightkey = data[3].value;
        payload.data[leftkey] = rightkey;
      }
    });

  (await elements) &&
    elements.forEach((data) => {
      if (data[0] === "users_permissions_user") {
        payload.data["users_permissions_user"] = {
          connect: [
            {
              id:
                JSON.parse(sessionStorage.getItem("userData")) != null
                  ? JSON.parse(sessionStorage.getItem("userData")).user.id
                  : "",
              position: { end: true },
            },
          ],
          disconnect: [],
        };

        return payload;
      }
    });

  (await elements) &&
    elements.forEach((data) => {
      if (data[0] === "organization") {
        payload.data["organization"] = {
          connect: [
            {
              id: data[3].value,
              position: { end: true },
            },
          ],
          disconnect: [],
        };

        return payload;
      }
    });

  return payload;
};

// Create an new entry in collection......

export const PublishData = async (payload, collectionName) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  console.log("PublishData called with:", {
    collectionName,
    payload,
    jwt: jwt ? "present" : "missing",
  });
  console.log(
    "PublishData payload structure:",
    JSON.stringify(payload, null, 2)
  );

  try {
    const response = await fetch(
      `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections/publish`,
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ collectionName, payload, jwt }),
      }
    );

    console.log(
      "PublishData request body:",
      JSON.stringify({ collectionName, payload, jwt }, null, 2)
    );

    console.log("PublishData response status:", response.status);
    console.log(
      "PublishData response headers:",
      Object.fromEntries(response.headers.entries())
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("PublishData error response (raw):", errorText);
      console.error("PublishData error response length:", errorText.length);
      console.error(
        "PublishData error response first 100 chars:",
        errorText.substring(0, 100)
      );

      // Try to parse as JSON, but handle HTML responses gracefully
      let errorData;
      try {
        errorData = JSON.parse(errorText);
        console.error("PublishData error response (parsed JSON):", errorData);
      } catch (parseError) {
        // If it's not JSON, it's probably HTML or plain text
        console.error("PublishData error response (not JSON):", errorText);
        errorData = { message: errorText };
      }

      throw new Error(
        `Server responded with ${response.status}: ${JSON.stringify(errorData)}`
      );
    }

    let data = await response.json();
    console.log("PublishData success response:", data);

    return data?.data;
  } catch (error) {
    console.error("PublishData failed:", error);
    throw error;
  }
};

export const doPluralize = (text) => {
  // let temp = text;
  if (text === pluralize(text)) {
    return `${text}`;
  }
  return pluralize(text);
};

export const shortenString = (str, maxLength) => {
  if (str.length > maxLength) {
    return str.substring(0, maxLength - 3) + "...";
  }
  return str;
};

export const DeleteOnedata = async (collectionName, id) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  const response = await fetch(
    `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections`,
    {
      method: "DELETE",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({ collectionName, entryId: id, jwt }),
    }
  );

  const data = await response.json();

  return data?.data;
};

// Update data function

export const UpdateOneData = async (collectionName, id, newData) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  let payload = newData;
  if (collectionName !== "users") {
    payload = {
      data: newData,
    };
  }

  const response = await fetch(
    `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections`,
    {
      method: "PUT",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({ collectionName, id, payload, jwt }),
    }
  );

  const data = await response.json();

  return data?.data;
};

/*

Inviting a new user to the CRCL
payload to pass -> email
Steps to creating a new user:

1. Does the User exist API needs to be done here.
2. Creating the new user (done)
3. The default payload must be:
  a. email (sent through the payload) (done)
  b. username (equal to the email) (done)
  c. password: try something complex. Current Default password is: N!0r@2023 (done)
  d. Making the connection link to this user that created the invite. 
4. Novu [keep it independent]
  a. Novu Email Template create
  b. Novu API create for invited user
  c. Make the new link inside this itself and call novu 9000 endpoint API to send the email.

  
*/

export const FilterUser = async (email) => {
  const data = await fetchCollection("users", "populate=*");

  const filteredData = data && data.find((item) => item.email === email);
  if (!filteredData) {
    return false;
  }
  return filteredData;
};

// Get all media files

export const GetAllMedia = async () => {
  const res = await fetchCollection("upload/files");
  return res;
};

// STRING LIMIT FUNCTION
export const StringLimit = (word, maxLength) => {
  if (word?.length > maxLength) {
    return word.substring(0, maxLength - 3) + "...";
  }
  return word;
};

// Function for Get the Items for a realtion (Like for Dropdown) :-
export const getItemForRelation = async (metricID, fieldContext = {}) => {
  console.log("🚀 getItemForRelation (name-first) called with:", { metricID, fieldContext });
  let values = [];

  // Determine the key we will use to query responses. This can be a metric name (string) or numeric id (legacy).
  const metricKey = (typeof metricID === "string" && metricID?.trim()) || (fieldContext?.metricName && String(fieldContext.metricName).trim()) || metricID;

  const isLikelyName = typeof metricKey === "string" && metricKey.trim().length > 0 && isNaN(Number(metricKey));
  const name = isLikelyName ? metricKey.trim() : null;

  // Helper: alias map for known naming differences
  const aliasMap = {
    "organizational unit": ["organization units", "organisational unit", "organisational units"],
    "organization units": ["organizational unit", "organisational unit", "organisational units"],
    "organisational unit": ["organization units", "organizational unit", "organisational units"],
    "organisational units": ["organization units", "organizational unit", "organisational unit"],
    "product or item": ["sustainability item sku", "product", "item", "sku", "sustainability sku"],
    "country/region": ["country or region", "country region", "country", "region"],
    "country or region": ["country/region", "country region", "country", "region"],
    "account": ["accounts"],
    "accounts": ["account"],
    "facility": ["facilities"],
    "facilities": ["facility"],
    "units": ["unit"],
    "unit": ["units"],
    "industrial process type": ["industrial process types"],
    "industrial process types": ["industrial process type"],
  };

  // Build candidate names: original, plural/singular, and aliases
  const buildCandidates = (baseName) => {
    const cands = new Set();
    if (!baseName) return Array.from(cands);
    const original = String(baseName).trim();
    cands.add(original);
    try {
      cands.add(pluralize(original));
      cands.add(pluralize.singular(original));
    } catch (_) {}
    const lower = original.toLowerCase();
    if (aliasMap[lower]) {
      aliasMap[lower].forEach((a) => cands.add(a));
    }
    return Array.from(cands);
  };

  const fields = ["Metric_Id", "metric_id", "Metric", "metric"];

  // Try NAME-based first (exact eqi for multiple candidates)
  if (name) {
    const candidates = buildCandidates(name);
    console.log("🔍 Name candidates for matching:", candidates);

    for (const cand of candidates) {
      for (const field of fields) {
        const filterURL = `filters[${field}][$eqi]=${cand}`;
        try {
          const data = await getDataWithFilterUrl("responses", filterURL);
          const responseData = data?.data || [];
          if (responseData.length > 0) {
            console.log(`✅ Matched by eqi: ${field} = "${cand}" → ${responseData.length} items`);
            const seen = new Set();
            for (const item of responseData) {
              if (seen.has(item.id)) continue;
              seen.add(item.id);
              const js = item?.attributes?.JsonSchema || {};
              // Extract name from flatData.Name if available, otherwise fall back to other fields
              const displayName = js.flatData?.Name || js.name || js.Name || js.title || js.description || `Item ${item.id}`;
              values.push({ id: item.id, name: String(displayName), jsonResponse: js });
            }
            values.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            return values;
          }
        } catch (err) {
          console.error(`❌ Error with eqi filter ${filterURL}:`, err);
        }
      }
    }

    // Fallback: contains-i using original and alias candidates
    for (const cand of candidates) {
      for (const field of fields) {
        const filterURL = `filters[${field}][$containsi]=${cand}`;
        try {
          const data = await getDataWithFilterUrl("responses", filterURL);
          const responseData = data?.data || [];
          if (responseData.length > 0) {
            console.log(`✅ Matched by containsi: ${field} contains "${cand}" → ${responseData.length} items`);
            const seen = new Set();
            for (const item of responseData) {
              if (seen.has(item.id)) continue;
              seen.add(item.id);
              const js = item?.attributes?.JsonSchema || {};
              // Extract name from flatData.Name if available, otherwise fall back to other fields
              const displayName = js.flatData?.Name || js.name || js.Name || js.title || js.description || `Item ${item.id}`;
              values.push({ id: item.id, name: String(displayName), jsonResponse: js });
            }
            values.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
            return values;
          }
        } catch (err) {
          console.error(`❌ Error with containsi filter ${filterURL}:`, err);
        }
      }
    }
  }

  // ID-based fallback
  const id = !isLikelyName ? metricKey : null;
  if (id != null) {
    const idFilters = [
      `filters[Metric_Id][$eqi]=${id}`,
      `filters[metric_id][$eqi]=${id}`,
      `filters[MetricId][$eqi]=${id}`,
      `filters[metricId][$eqi]=${id}`,
    ];
    for (const filterURL of idFilters) {
      try {
        const data = await getDataWithFilterUrl("responses", filterURL);
        const responseData = data?.data || [];
        if (responseData.length === 0) continue;
        const seen = new Set();
        for (const item of responseData) {
          if (seen.has(item.id)) continue;
          seen.add(item.id);
          const js = item?.attributes?.JsonSchema || {};
          // Extract name from flatData.Name if available, otherwise fall back to other fields
          const displayName = js.flatData?.Name || js.name || js.Name || js.title || js.description || `Item ${item.id}`;
          values.push({ id: item.id, name: String(displayName), jsonResponse: js });
        }
        values.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        console.log(`✅ getItemForRelation success via ${filterURL} → ${values.length} items`);
        return values;
      } catch (err) {
        console.error(`❌ getItemForRelation error with filter ${filterURL}:`, err);
      }
    }
  }

  console.log("⚠️ getItemForRelation: no responses found for", metricKey);
  return [];
};

export async function updateFormSchemaWithRealtion(
  formSchema,
  index,
  setFormSchema
) {
  console.log("🔄 updateFormSchemaWithRealtion called:", {
    index,
    fieldConfig: formSchema[index][1],
  });

  if (formSchema[index][1].values === undefined || formSchema[index][1].values.length === 0) {
    // Guard against loops
    let newFormSchema = [...formSchema];
    if (!newFormSchema[index][1]._isProcessing) {
      newFormSchema[index][1] = { ...newFormSchema[index][1], _isProcessing: true };
      setFormSchema(newFormSchema);
    }

    const fieldConfig = formSchema[index][1];
    const relatedToCSV = fieldConfig.relatedTo || fieldConfig.relatedto || "";
    const relatedToList = relatedToCSV
      .split(",")
      .map((s) => s.trim())
      .filter((s) => !!s);

    try {
      // PRIORITY 1: Use relatedTo metric names directly against responses (no cap)
      if (relatedToList.length > 0) {
        console.log("📌 Using relatedTo metric names for relation:", relatedToList);
        const results = await Promise.all(
          relatedToList.map((metricName) =>
            getItemForRelation(metricName, {
              metricName,
              title: fieldConfig.title,
              type: fieldConfig.type,
            })
          )
        );
        const merged = [];
        const seen = new Set();
        for (const arr of results) {
          for (const item of arr || []) {
            if (seen.has(item.id)) continue;
            seen.add(item.id);
            merged.push(item);
          }
        }

        let updatedFormSchema = [...formSchema];
        updatedFormSchema[index][1] = {
          ...updatedFormSchema[index][1],
          values: merged,
          _isProcessing: false,
        };
        setFormSchema(updatedFormSchema);
        return;
      }

      // PRIORITY 2 (fallback): existing metric lookup by name → id, then fetch
      const metricName = fieldConfig.metricname || fieldConfig.metricName;
      const relatedToField = fieldConfig.relatedTo || fieldConfig.relatedto;

      if (metricName && relatedToField) {
        console.log("🔍 Fallback path: resolving metric by Name:", metricName);
        let metricFilterUrl = `filters[Name][$eqi]=${metricName}`;
        let metricData = await getDataWithFilterUrl("metrics", metricFilterUrl);
        if (!metricData?.data?.length) {
          const broaderFilterUrl = `filters[Name][$contains]=${(metricName || "").split(" ")[0]}`;
          metricData = await getDataWithFilterUrl("metrics", broaderFilterUrl);
        }

        if (metricData?.data?.length) {
          const metricId = metricData.data[0].id;
          const relationData = await getItemForRelation(metricId, {
            fieldTitle: fieldConfig.title,
            fieldType: fieldConfig.type,
            metricName,
          });
          let updatedFormSchema = [...formSchema];
          updatedFormSchema[index][1] = {
            ...updatedFormSchema[index][1],
            values: relationData || [],
            _isProcessing: false,
          };
          setFormSchema(updatedFormSchema);
        } else {
          let updatedFormSchema = [...formSchema];
          updatedFormSchema[index][1] = {
            ...updatedFormSchema[index][1],
            values: [],
            _isProcessing: false,
          };
          setFormSchema(updatedFormSchema);
        }
      } else {
        console.log("⚠️ No relatedTo or metricName provided; setting empty values");
        let updatedFormSchema = [...formSchema];
        updatedFormSchema[index][1] = {
          ...updatedFormSchema[index][1],
          values: [],
          _isProcessing: false,
        };
        setFormSchema(updatedFormSchema);
      }
    } catch (error) {
      console.error("❌ Error in updateFormSchemaWithRealtion:", error);
      let updatedFormSchema = [...formSchema];
      updatedFormSchema[index][1] = {
        ...updatedFormSchema[index][1],
        values: [],
        _isProcessing: false,
      };
      setFormSchema(updatedFormSchema);
    }
  } else {
    console.log("⏭️ Skipping API call - values already exist");
  }
}

export const capitalize = (s) => s && s[0].toUpperCase() + s.slice(1);

export const separateDasFromUrl = (value) => {
  return value?.replaceAll("-", " ");
};

// ROLES APIs
export const getAllRoles = async () => {
  const res = await fetchCollection("users-permissions/roles");

  let data = res.roles;

  const FilterData = data && data.find((role) => role.name === "Public");

  return FilterData;
};

// Keys Generate and Stored in DB

export const submitAPIKey = async (api_key, key_name, user_email) => {
  const PayloadData = {
    api_key: api_key,
    users_permissions_user: user_email,
    Name: key_name,
  };

  let result = await PublishData({ data: PayloadData }, "key");

  return result;
};

export const UpdateSingleCollectionData = async (MetricName, newData) => {
  const payload = { data: newData };
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  const response = await fetch(
    `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections/single`,
    {
      method: "PUT",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({ MetricName, payload, jwt }),
    }
  );

  const data = await response.json();
  return data?.data;
};

export const getSingleCollectionData = async (collectionName) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  const response = await fetch(
    `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections/single`,
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
      },

      body: JSON.stringify({ collectionName, jwt }),
    }
  );

  const data = await response.json();

  return data?.data;
};

// FILE UPLOAD TO STRAPI
export const uploadFile = async (file) => {
  const res = await fetch(
    `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/files/upload`,
    {
      method: "POST",
      mode: "cors",
      cache: "no-cache",
      body: file,
    }
  );

  let data = await res.json();
  return data?.data;
};

export const InviteUser = async (payload) => {
  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/send-email`;

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      // Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify(payload),
  });

  let data = response.json();

  return data;
};

export const sendSurvey = async (payload) => {
  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/send-survey`;

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      // Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify(payload),
  });

  let data = await response.json();

  return data;
};

export const updateUserLastOrg = async (id, OrgUrl, orgId = 0) => {
  const userRole = await getUserRoleFromOrg(orgId, id);

  const data = {
    userRole: userRole,
    LastOrg: OrgUrl,
    LastOrgId: orgId,
  };

  const result = await UpdateOneData("users", id, data);
  const { Password2, ...userDataWithoutPassword } = result;
  let userData = JSON.parse(sessionStorage.getItem("userData"));

  userData["user"] = userDataWithoutPassword;
  sessionStorage.setItem("userData", JSON.stringify(userData));

  return result;
};

// Get Status,CurrentCount,RequiredCount of Metric Responses for an organization
export const getStatusOfMetric = async (
  metricName,
  OrgId,
  metricID,
  frequencyCounter
) => {
  const url = `filters[Organization][id][$eqi]=${OrgId}&filters[Metric_Id][$eqi]=${metricName}&sort[0]=updatedAt:desc`;

  const result = await getFilterDataWithPagination("responses", url, 1, 1);
  const lastUpdatedAt = result?.data[0]?.attributes?.updatedAt;
  const currCount = result?.meta?.pagination.total || 0;
  let requiredCount = 0;

  frequencyCounter?.forEach((element) => {
    if (element.metric_id === metricID) {
      requiredCount = element?.counter;
    }
  });

  const status = currCount >= requiredCount ? "Completed" : "Incompleted";

  return {
    Status: status,
    RequiredCount: requiredCount,
    CurrentCount: currCount,
    lastUpdatedAt: lastUpdatedAt,
  };
};

export const createOrg = async (payload) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/create-org`;

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      // Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify({ ...payload, jwt }),
  });

  if (!response.ok) {
    console.error(`API Error: ${response.status} ${response.statusText}`);
    const errorText = await response.text();
    console.error("Error response body:", errorText);
    throw new Error(
      `Server error: ${response.status} - ${response.statusText}`
    );
  }

  let data;
  try {
    data = await response.json();
  } catch (jsonError) {
    console.error("Failed to parse JSON response:", jsonError);
    const responseText = await response.text();
    console.error("Response text:", responseText);
    throw new Error("Invalid JSON response from server");
  }

  return data;
};

export const deleteOrg = async (payload) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }

  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/delete-org`;

  console.log("Delete org request:", { URL, payload, jwt });

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      // Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify({ ...payload, jwt }),
  });

  console.log("Delete org response status:", response.status);

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  let data = await response.json();
  console.log("Delete org response data:", data);

  return data;
};

export const sendResetPasswordEmail = async (payload) => {
  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/reset-password-email`;

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      // Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify(payload),
  });

  let data = await response.json();

  return data;
};

export const sendCreateAccountOTP = async (payload) => {
  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/send-create-otp`;

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      // Authorization: `Bearer ${jwt}`,
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify(payload),
  });

  let data = await response.json();

  return data;
};

export const sendSubmissionUpdationAlert = async (payload) => {
  let jwt;

  if (JSON.parse(sessionStorage.getItem("userData"))) {
    jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
  } else {
    jwt = "";
  }
  var URL = `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/send-submission-alert`;

  const response = await fetch(URL, {
    method: "POST",
    mode: "cors",
    cache: "no-cache",
    redirect: "follow",
    headers: {
      "Content-Type": "application/json", // Specify the content type as JSON
    },
    referrerPolicy: "no-referrer",
    body: JSON.stringify({ ...payload, jwt }),
  });

  let data = await response.json();

  return data;
};

export const fetchReportChunk = async (reportId = null) => {
  try {
    let jwt;

    if (JSON.parse(sessionStorage.getItem("userData"))) {
      jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
    } else {
      jwt = "";
    }
    const response = await fetch(
      `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/get-report`,
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify({ reportId: reportId, jwt }),
      }
    );

    const data = await response.json();

    return data?.data;
  } catch (error) {
    console.error("Error fetching data", error);
    // return [];
  }
};

export const generateReportChunk = async (reportData = null) => {
  try {
    let jwt;

    if (JSON.parse(sessionStorage.getItem("userData"))) {
      jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
    } else {
      jwt = "";
    }
    const response = await fetch(
      `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/report`,
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },

        body: JSON.stringify(reportData),
      }
    );

    const data = await response.json();

    return data?.data;
  } catch (error) {
    console.error("Error fetching data", error);
    // return [];
  }
};

// Audit Log Functions
export const fetchAuditLogs = async (filterUrl = null) => {
  try {
    let jwt;

    if (JSON.parse(sessionStorage.getItem("userData"))) {
      jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
    } else {
      jwt = "";
    }

    const requestBody = { collectionName: "audit-log", filterUrl, jwt };
    console.log("fetchAuditLogs request:", {
      url: `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections`,
      filterUrl,
      hasJwt: !!jwt,
    });

    const response = await fetch(
      `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/collections`,
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    console.log("fetchAuditLogs response status:", response.status);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    console.log("fetchAuditLogs response data:", data);

    return data?.data;
  } catch (error) {
    console.error("Error fetching audit logs", error);
    return [];
  }
};

export const createAuditLog = async (auditEntry) => {
  try {
    let jwt;

    if (JSON.parse(sessionStorage.getItem("userData"))) {
      jwt = JSON.parse(sessionStorage.getItem("userData")).jwt;
    } else {
      jwt = "";
    }

    const response = await fetch(
      `${process.env.REACT_APP_MIDDLEWARE_IP_ADDRESS}/audit-logs`,
      {
        method: "POST",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify(auditEntry),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error creating audit log", error);
    throw error;
  }
};

export const getAuditLogsWithFilter = async (filterUrl) => {
  const url = `${filterUrl}&populate=*&pagination[limit]=1000`;
  const result = await fetchAuditLogs(url);
  return result;
};

export const getAuditLogsByDateRange = async (startDate, endDate, orgId) => {
  const filterUrl = `filters[Organization][id][$eqi]=${orgId}&filters[timestamp][$gte]=${startDate}&filters[timestamp][$lte]=${endDate}`;
  return await getAuditLogsWithFilter(filterUrl);
};

export const getAuditLogsByUser = async (userId, orgId) => {
  const filterUrl = `filters[Organization][id][$eqi]=${orgId}&filters[userId][$eqi]=${userId}`;
  return await getAuditLogsWithFilter(filterUrl);
};

export const getAuditLogsByAction = async (action, orgId) => {
  const filterUrl = `filters[Organization][id][$eqi]=${orgId}&filters[action][$eqi]=${action}`;
  return await getAuditLogsWithFilter(filterUrl);
};

export const getAuditLogsByStatus = async (status, orgId) => {
  const filterUrl = `filters[Organization][id][$eqi]=${orgId}&filters[status][$eqi]=${status}`;
  return await getAuditLogsWithFilter(filterUrl);
};

// Function to clean up existing metrics by removing guidance, overview, and reference fields
export const cleanUpExistingMetrics = async (orgId) => {
  try {
    // Get all metrics for the organization
    const filterUrl = `filters[Organization][id][$eqi]=${orgId}`;
    const metrics = await getDataWithFilterUrl("metrics", filterUrl);

    if (!metrics?.data || metrics.data.length === 0) {
      console.log("No metrics found for organization:", orgId);
      return { success: true, message: "No metrics found to clean up" };
    }

    let cleanedCount = 0;
    let errorCount = 0;

    for (const metric of metrics.data) {
      try {
        const jsonSchema = metric.attributes.JsonSchema;

        if (jsonSchema && jsonSchema.properties) {
          // Clean up the JSON schema
          const cleanedProperties = {};

          Object.keys(jsonSchema.properties).forEach((fieldName) => {
            const field = jsonSchema.properties[fieldName];
            const cleanedField = { ...field };

            // Remove guidance, overview, and reference fields
            delete cleanedField.guidance;
            delete cleanedField.overview;
            delete cleanedField.reference;

            cleanedProperties[fieldName] = cleanedField;
          });

          const cleanedJsonSchema = {
            ...jsonSchema,
            properties: cleanedProperties,
          };

          // Update the metric with cleaned JSON schema
          await UpdateOneData("metrics", metric.id, {
            JsonSchema: cleanedJsonSchema,
          });

          cleanedCount++;
          console.log(`Cleaned metric: ${metric.attributes.Name}`);
        }
      } catch (error) {
        console.error(
          `Error cleaning metric ${metric.attributes.Name}:`,
          error
        );
        errorCount++;
      }
    }

    return {
      success: true,
      cleanedCount,
      errorCount,
      message: `Successfully cleaned ${cleanedCount} metrics. ${errorCount} errors occurred.`,
    };
  } catch (error) {
    console.error("Error in cleanUpExistingMetrics:", error);
    return {
      success: false,
      error: error.message,
      message: "Failed to clean up metrics",
    };
  }
};

